/**
 * API Utility Functions
 */

class ApiClient {
    constructor() {
        // Check both admin and frontend data objects
        const data = window.shopLocalizer || window.shopExplorerData || window.shopExplorerFrontendData || {};
        this.baseUrl = data.apiUrl || '/wp-json/shop-explorer/v1';
        this.nonce = data.nonce || '';
        
    }

    /**
     * Make API request
     * @param {string} endpoint 
     * @param {object} options 
     * @returns {Promise}
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;

        const defaultOptions = {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': this.nonce,
            },
            credentials: 'same-origin',
        };

        const config = {
            ...defaultOptions,
            ...options,
            headers: {
                ...defaultOptions.headers,
                ...options.headers,
            },
        };

        // Debug logging
        console.log('API Request:', {
            url,
            method: config.method || 'GET',
            nonce: this.nonce,
            hasNonce: !!this.nonce,
            headers: config.headers
        });

        try {
            const response = await fetch(url, config);
            const data = await response.json();

            if (!response.ok) {
                console.error('API Response Error:', {
                    status: response.status,
                    statusText: response.statusText,
                    data
                });
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('API Request failed:', error);
            throw error;
        }
    }

    /**
     * GET request
     * @param {string} endpoint 
     * @param {object} params 
     * @returns {Promise}
     */
    async get(endpoint, params = {}) {
        // Filter out undefined, null, and empty string values
        const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {});

        const queryString = new URLSearchParams(filteredParams).toString();
        const url = queryString ? `${endpoint}?${queryString}` : endpoint;

        return this.request(url, {
            method: 'GET',
        });
    }

    /**
     * POST request
     * @param {string} endpoint 
     * @param {object} data 
     * @returns {Promise}
     */
    async post(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    /**
     * PUT request
     * @param {string} endpoint 
     * @param {object} data 
     * @returns {Promise}
     */
    async put(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    /**
     * DELETE request
     * @param {string} endpoint 
     * @returns {Promise}
     */
    async delete(endpoint) {
        return this.request(endpoint, {
            method: 'DELETE',
        });
    }

    // Authentication methods
    async login(username, password, remember = false) {
        return this.post('/auth/login', { username, password, remember });
    }

    async logout() {
        return this.post('/auth/logout');
    }

    async getCurrentUser() {
        return this.get('/auth/me');
    }

    // Settings methods
    async getSettings() {
        return this.get('/settings');
    }

    async updateSettings(settings) {
        return this.post('/settings', settings);
    }

    async exportSettings() {
        return this.get('/settings/export');
    }

    async importSettings(settings) {
        return this.post('/settings/import', settings);
    }

    async resetSettings() {
        return this.post('/settings/reset');
    }

    // Orders methods
    async getOrders(params = {}) {
        return this.get('/orders', params);
    }

    async getOrder(id) {
        return this.get(`/orders/${id}`);
    }

    async updateOrder(id, data) {
        return this.put(`/orders/${id}`, data);
    }

    // Products methods
    async getProducts(params = {}) {
        return this.get('/products', params);
    }

    async updateProduct(id, data) {
        return this.put(`/products/${id}`, data);
    }

    async syncProducts() {
        return this.post('/sync/products');
    }

    // Dashboard methods
    async getDashboardStats() {
        return this.get('/dashboard/stats');
    }

    async getRecentActivity() {
        return this.get('/dashboard/activity');
    }

    async getConfiguration() {
        return this.get('/configuration');
    }

    // Monitoring methods
    async getMonitoringDashboard() {
        return this.get('/monitoring/dashboard');
    }

    // Vendor methods
    async getVendors(params = {}) {
        return this.get('/vendors', params);
    }

    async getVendor(id) {
        return this.get(`/vendors/${id}`);
    }

    async updateVendor(id, data) {
        return this.put(`/vendors/${id}`, data);
    }

    async createVendor(data) {
        return this.post('/vendors', data);
    }

    async deleteVendor(id) {
        return this.delete(`/vendors/${id}`);
    }

    async createVendorSheets(vendorId, options = {}) {
        return this.post(`/vendors/${vendorId}/sheets`, options);
    }

    async syncVendorData(vendorId, options = {}) {
        return this.post(`/vendors/${vendorId}/sync`, options);
    }

    // Sync methods - restored with proper endpoint
    async getSyncStatus() {
        return this.get('/sync/status');
    }

    async getSyncHistory(params = {}) {
        return this.get('/sync/history', params);
    }

    async triggerSync(type, options = {}) {
        return this.post(`/sync/${type}`, options);
    }

    // Bulk operations
    async bulkUpdateOrders(orderIds, data) {
        return this.post('/orders/bulk-update', { order_ids: orderIds, data });
    }

    async bulkUpdateProducts(productIds, data) {
        return this.post('/products/bulk-update', { product_ids: productIds, data });
    }

    async bulkUpdateStock(stockUpdates) {
        return this.post('/products/bulk-stock-update', { stock_updates: stockUpdates });
    }

    // Search methods
    async searchOrders(query, filters = {}) {
        return this.get('/search/orders', { q: query, ...filters });
    }

    async searchProducts(query, filters = {}) {
        return this.get('/search/products', { q: query, ...filters });
    }

    // Export methods
    async exportOrders(filters = {}, format = 'csv') {
        return this.get('/export/orders', { ...filters, format });
    }

    async exportProducts(filters = {}, format = 'csv') {
        return this.get('/export/products', { ...filters, format });
    }

    // Google Sheets methods
    async getSHOPEX_GoogleSheetsAuth() {
        return this.get('/google-sheets/auth');
    }

    async authorizeGoogleSheets(code, credentials = null) {
        const data = { code };
        if (credentials) {
            data.credentials = credentials;
        }
        return this.post('/google-sheets/auth', data);
    }

    async revokeSHOPEX_GoogleSheetsAuth() {
        return this.delete('/google-sheets/auth');
    }

    async testGoogleSheetsConnection(spreadsheetUrl = null) {
        const params = spreadsheetUrl ? { spreadsheet_url: spreadsheetUrl } : {};
        return this.get('/google-sheets/test', params);
    }

    async uploadGoogleSheetsCredentials(credentials) {
        return this.post('/google-sheets/credentials', { credentials: JSON.stringify(credentials) });
    }

    async getGoogleSpreadsheets() {
        return this.get('/google-sheets/spreadsheets');
    }

    async createGoogleSpreadsheet(title, type) {
        return this.post('/google-sheets/spreadsheets', { title, type });
    }

    async testSheetAccess(spreadsheet_url, sheet_name) {
        return this.post('/google-sheets/test-sheet', { spreadsheet_url, sheet_name });
    }

    async configureSheet(spreadsheet_url, sheet_name, sheet_type) {
        return this.post('/google-sheets/configure-sheet', { spreadsheet_url, sheet_name, sheet_type });
    }

    // System Health and Monitoring methods
    async getSystemHealth() {
        return this.get('/monitoring/health');
    }

    async getSyncLogs(params = {}) {
        return this.get('/monitoring/logs', params);
    }

    async getPerformanceMetrics() {
        return this.get('/monitoring/metrics');
    }

    async clearSyncLogs() {
        return this.delete('/monitoring/logs');
    }

    async runHealthCheck() {
        return this.post('/monitoring/health-check');
    }

    // Additional Vendor methods
    async syncVendors() {
        return this.post('/sync/vendors');
    }

    async exportVendors(filters = {}, format = 'csv') {
        return this.get('/export/vendors', { ...filters, format });
    }

    async createVendorSheet(vendorId) {
        return this.post(`/vendors/${vendorId}/create-sheet`);
    }

    // Google Sheets Configuration methods
    async saveGoogleSheetsConfig(configData) {
        return this.post('/google-sheets/config', configData);
    }

    async uploadGoogleCredentials(credentials) {
        return this.uploadGoogleSheetsCredentials(credentials);
    }

    // Order Sync methods
    async getOrderSyncFields() {
        return this.get('/orders/sync-fields');
    }

    async updateOrderSyncFields(fieldSettings) {
        return this.post('/orders/sync-fields', fieldSettings);
    }

    async syncAllOrders(spreadsheetUrl, sheetName = 'Orders') {
        return this.post('/orders/sync-all', { 
            spreadsheet_url: spreadsheetUrl, 
            sheet_name: sheetName 
        });
    }

    async syncSingleOrder(orderId, spreadsheetUrl, sheetName = 'Orders') {
        return this.post(`/orders/${orderId}/sync`, { 
            spreadsheet_url: spreadsheetUrl, 
            sheet_name: sheetName 
        });
    }

    async getProductSyncFields() {
        return this.get('/products/sync-fields');
    }

    async updateProductSyncFields(fieldSettings) {
        return this.post('/products/sync-fields', fieldSettings);
    }

    async syncAllProducts(spreadsheetUrl, sheetName = 'Products') {
        return this.post('/products/sync-all', { 
            spreadsheet_url: spreadsheetUrl, 
            sheet_name: sheetName 
        });
    }

    async syncSingleProduct(productId, spreadsheetUrl, sheetName = 'Products') {
        return this.post(`/products/${productId}/sync`, { 
            spreadsheet_url: spreadsheetUrl, 
            sheet_name: sheetName 
        });
    }

    async getCustomerSyncFields() {
        return this.get('/customers/sync-fields');
    }

    async updateCustomerSyncFields(fieldSettings) {
        return this.post('/customers/sync-fields', fieldSettings);
    }

    async syncAllCustomers(spreadsheetUrl, sheetName = 'Customers') {
        return this.post('/customers/sync-all', { 
            spreadsheet_url: spreadsheetUrl, 
            sheet_name: sheetName 
        });
    }

    // Frontend-specific methods
    async createOrder(orderData) {
        return this.post('/orders', orderData);
    }

    async createProduct(productData) {
        return this.post('/products', productData);
    }

    async getCustomers(params = {}) {
        return this.get('/customers', params);
    }

    async getCountries() {
        return this.get('/countries');
    }

    async getStates(country) {
        return this.get('/states', { country });
    }

    async getProductCategories() {
        return this.get('/product-categories');
    }

    async getProductTags() {
        return this.get('/product-tags');
    }

    async getProductAttributes() {
        return this.get('/product-attributes');
    }

    async createProductCategory(categoryData) {
        return this.post('/product-categories', categoryData);
    }

    async createProductTag(tagData) {
        return this.post('/product-tags', tagData);
    }

    async createProductAttribute(attributeData) {
        return this.post('/product-attributes', attributeData);
    }

    async getAttributeTerms(attributeId) {
        return this.get(`/product-attributes/${attributeId}/terms`);
    }

    async createAttributeTerm(attributeId, termData) {
        return this.post(`/product-attributes/${attributeId}/terms`, termData);
    }

    async getProductBrands() {
        return this.get('/product-brands');
    }

    async createProductBrand(brandData) {
        return this.post('/product-brands', brandData);
    }

    async bulkUpdateOrderStatus(orderIds, status) {
        return this.post('/orders/bulk-status-update', { 
            order_ids: orderIds, 
            status 
        });
    }

    async bulkUpdateProductStatus(productIds, status) {
        return this.post('/products/bulk-status-update', { 
            product_ids: productIds, 
            status 
        });
    }

    async bulkUpdateProductStock(stockUpdates) {
        return this.post('/products/bulk-stock-update', { 
            stock_updates: stockUpdates
        });
    }

    // Batch processing methods for optimized bulk operations
    async batchUpdateOrders(orderIds, data, batchSize = 5000) {
        return this.post('/orders/batch-update', { 
            order_ids: orderIds, 
            data,
            batch_size: batchSize
        });
    }

    async batchUpdateProducts(productIds, data, batchSize = 5000) {
        return this.post('/products/batch-update', { 
            product_ids: productIds, 
            data,
            batch_size: batchSize
        });
    }

    async batchUpdateOrderStatus(orderIds, status, batchSize = 5000) {
        return this.post('/orders/batch-status-update', { 
            order_ids: orderIds, 
            status,
            batch_size: batchSize
        });
    }

    async batchUpdateProductStatus(productIds, status, batchSize = 5000) {
        return this.post('/products/batch-status-update', { 
            product_ids: productIds, 
            status,
            batch_size: batchSize
        });
    }

    async batchUpdateProductStock(stockUpdates, batchSize = 5000) {
        return this.post('/products/batch-stock-update', { 
            stock_updates: stockUpdates,
            batch_size: batchSize
        });
    }

    async batchDeleteOrders(orderIds, force = false, batchSize = 5000) {
        return this.post('/orders/batch-delete', { 
            order_ids: orderIds,
            force: force,
            batch_size: batchSize
        });
    }

    async batchDeleteProducts(productIds, force = false, batchSize = 5000) {
        return this.post('/products/batch-delete', { 
            product_ids: productIds,
            force: force,
            batch_size: batchSize
        });
    }

    // Batch loading methods for initial data fetch
    async batchGetOrders(orderIds, batchSize = 100) {
        return this.post('/orders/batch-get', { 
            order_ids: orderIds,
            batch_size: batchSize
        });
    }

    async batchGetProducts(productIds, batchSize = 100) {
        return this.post('/products/batch-get', { 
            product_ids: productIds,
            batch_size: batchSize
        });
    }

    async bulkDeleteProducts(productIds, force = false) {
        return this.post('/products/bulk-delete', { 
            product_ids: productIds,
            force: force
        });
    }

    async deleteProduct(id, force = false) {
        return this.delete(`/products/${id}?force=${force}`);
    }

    async trashProduct(id) {
        return this.post(`/products/${id}/trash`);
    }

    async restoreProduct(id) {
        return this.post(`/products/${id}/restore`);
    }

    async bulkDeleteOrders(orderIds, force = false) {
        return this.post('/orders/bulk-delete', { 
            order_ids: orderIds,
            force 
        });
    }

    async getProduct(id) {
        return this.get(`/products/${id}`);
    }

    async deleteOrder(id, force = false) {
        return this.delete(`/orders/${id}?force=${force}`);
    }

    // Order Notes
    async getOrderNotes(orderId) {
        return this.get(`/orders/${orderId}/notes`);
    }

    async addOrderNote(orderId, content, noteType = 'private') {
        return this.post(`/orders/${orderId}/notes`, {
            content,
            note_type: noteType
        });
    }

    async updateOrderNote(orderId, noteId, content) {
        return this.put(`/orders/${orderId}/notes/${noteId}`, {
            content
        });
    }

    async deleteOrderNote(orderId, noteId) {
        return this.delete(`/orders/${orderId}/notes/${noteId}`);
    }

    // Order Refund
    async refundOrder(orderId, amount, reason = '') {
        return this.post(`/orders/${orderId}/refund`, {
            amount,
            reason
        });
    }

    async getOrderStatuses() {
        return this.get('/order-statuses');
    }

    async getPaymentMethods() {
        return this.get('/payment-methods');
    }

    async uploadProductImage(formData) {
        // Use REST API endpoint for image upload (like Other plugin)
        const url = `${this.baseUrl}/wordpress/media`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-WP-Nonce': this.nonce,
                },
                body: formData,
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                return {
                    success: true,
                    attachment_id: result.attachment.id,
                    url: result.attachment.url,
                    alt: result.attachment.alt || '',
                    title: result.attachment.title || '',
                    name: result.attachment.filename || ''
                };
            } else {
                throw new Error(result.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    // Frontend Dashboard Settings
    async getFrontendSettings() {
        return this.get('/frontend-settings');
    }

    async saveFrontendSettings(settings) {
        return this.post('/frontend-settings', settings);
    }

    // Varitions API 
    async getProductVariations(productId) {
        return this.get(`/products/${productId}/variations`);
    }

    async createProductVariation(productId, variationData) {
        return this.post(`/products/${productId}/variations`, variationData);
    }

    async updateProductVariation(productId, variationId, variationData) {
        return this.put(`/products/${productId}/variations/${variationId}`, variationData);
    }

    async deleteProductVariation(productId, variationId) {
        return this.delete(`/products/${productId}/variations/${variationId}`);
    }

    async generateProductVariations(productId, attributes) {
        return this.post(`/products/${productId}/variations/generate`, { attributes });
    }


    // Create singleton instance
    // Categories methods
    async getCategories() {
        return this.get('/categories');
    }

    async getCategory(id) {
        return this.get(`/categories/${id}`);
    }

    async createCategory(data) {
        return this.post('/categories', data);
    }

    async updateCategory(id, data) {
        return this.put(`/categories/${id}`, data);
    }

    async deleteCategory(id) {
        return this.delete(`/categories/${id}`);
    }

    // Tags methods
    async getTags() {
        return this.get('/tags');
    }

    async getTag(id) {
        return this.get(`/tags/${id}`);
    }

    async createTag(data) {
        return this.post('/tags', data);
    }

    async updateTag(id, data) {
        return this.put(`/tags/${id}`, data);
    }

    async deleteTag(id) {
        return this.delete(`/tags/${id}`);
    }

    // Brands methods
    async getBrands() {
        return this.get('/brands');
    }

    async getBrand(id) {
        return this.get(`/brands/${id}`);
    }

    async createBrand(data) {
        return this.post('/brands', data);
    }

    async updateBrand(id, data) {
        return this.put(`/brands/${id}`, data);
    }

    async deleteBrand(id) {
        return this.delete(`/brands/${id}`);
    }

    // Attributes methods
    async getAttributes() {
        return this.get('/attributes');
    }

    async getAttribute(id) {
        return this.get(`/attributes/${id}`);
    }

    async createAttribute(data) {
        return this.post('/attributes', data);
    }

    async updateAttribute(id, data) {
        return this.put(`/attributes/${id}`, data);
    }

    async deleteAttribute(id) {
        return this.delete(`/attributes/${id}`);
    }

    // Attribute Terms methods
    async getAttributeTerms(attributeId) {
        return this.get(`/attributes/${attributeId}/terms`);
    }

    async createAttributeTerm(attributeId, data) {
        return this.post(`/attributes/${attributeId}/terms`, data);
    }

    async updateAttributeTerm(attributeId, termId, data) {
        return this.put(`/attributes/${attributeId}/terms/${termId}`, data);
    }

    async deleteAttributeTerm(attributeId, termId) {
        return this.delete(`/attributes/${attributeId}/terms/${termId}`);
    }

    // Reviews methods
    async getReviews(params = {}) {
        return this.get('/reviews', params);
    }

    async getReview(id) {
        return this.get(`/reviews/${id}`);
    }

    async updateReviewStatus(id, status) {
        return this.put(`/reviews/${id}/status`, { status });
    }

    async deleteReview(id) {
        return this.delete(`/reviews/${id}`);
    }

    async replyToReview(id, reply) {
        return this.post(`/reviews/${id}/reply`, { reply });
    }

    async getReviewSettings() {
        return this.get('/reviews/settings');
    }

    async updateReviewSettings(settings) {
        return this.put('/reviews/settings', settings);
    }

    // ── AI Integration methods ──────────────────────────────────────────────
    async getAIProviders() {
        return this.get('/ai/providers');
    }

    async getAISettings() {
        return this.get('/ai/settings');
    }

    async saveAISettings(settings) {
        return this.post('/ai/settings', settings);
    }

    async testAIConnection(payload) {
        return this.post('/ai/test', payload);
    }

    /**
     * Generate AI content
     * @param {'product'|'order_note'} type
     * @param {string} prompt
     * @param {object} context  optional extra context
     */
    async generateAIContent(type, prompt, context = {}) {
        return this.post('/ai/generate', { type, prompt, context });
    }

    async analyzeRevenue(analyticsData, question = '', history = []) {
        return this.post('/ai/analyze-revenue', {
            analytics_data: analyticsData,
            question,
            history,
        });
    }
}

const api = new ApiClient();

export { api };
export default api; 
    